import React from 'react';
import numeral from 'numeral';

const DEFAULT_CURRENCY_PRECISION = 2;

const CODES: Record<string, number> = {
  ADA: 0x20b3,
  AZN: 0x20bc,
  BCH: 0x0243,
  BNB: 0x25c7,
  BTC: 0x20bf,
  CNY: 0xa5,
  DOGE: 0x189,
  ETH: 0x039e,
  EUR: 0x20ac,
  KZT: 0x20b8,
  LTC: 0x141,
  PHP: 0x20b1,
  RUB: 0x20bd,
  THB: 0xe3f,
  UAH: 0x20b4,
  USD: 0x24,
  USDT: 0x20ae,
  VND: 0x20ab,
};

const precisions: Record<string, number> = {
  BNB: 6,
  BTC: 8,
  ETH: 8,
  LTC: 8,
};

export const currencies = ['USD', 'EUR', 'RUB', 'UAH', 'CNY', 'KZT'] as const;

export type Currency = typeof currencies[number];

export const currencyPrecision = (currency: string): number => precisions[currency] || DEFAULT_CURRENCY_PRECISION;

const moneyFormat = (currency: string) => (currencyPrecision(currency) > 2 ? '0,0.0000' : '0,0.00');

const moneyAccurateFormat = (currency: string) => `0,0.${Array(currencyPrecision(currency) + 1).join('0')}`;

const charByCode = (currency: string) => (CODES[currency] ? String.fromCharCode(CODES[currency]) : currency);

const currencySymbol = (currency: string): string => {
  switch (currency) {
    case 'BUSD':
      return `B${charByCode('USD')}`;
    case 'CAD':
      return `C${charByCode('USD')}`;
    case 'IDR':
      return 'Rp';
    case 'MYR':
      return 'RM';
    case 'mBTC':
      return `m${charByCode('BTC')}`;
    case 'USDC':
      return `${charByCode('USD')}C`;
    case 'USDCS':
      return `${charByCode('USD')}CS`;
    case 'USDTB':
      return `${charByCode('USDT')}B`;
    case 'USDTE':
      return `${charByCode('USDT')}E`;
    case 'USDTS':
      return `${charByCode('USDT')}S`;
    case 'USDTT':
      return `${charByCode('USDT')}T`;
    case 'USDTTON':
      return `${charByCode('USDT')}TON`;
    case 'XRP':
      return 'X';
    default:
      return charByCode((currency || '').toUpperCase());
  }
};

const order = (currency: string, symbol: string, value: string) =>
  currency === 'RUB' ? `${value}${symbol}` : `${symbol}${value}`;

const moneyStr = (amount: number, currency: string, formatString?: string): string => {
  const sign = amount < 0 ? '-' : '';
  const format = formatString || moneyFormat(currency);
  return sign + order(currency, currencySymbol(currency), numeral(Math.abs(amount)).format(format));
};

const money = (amount: number, currency: string, formatString?: string): JSX.Element => (
  <span className="money" data-amount={amount} data-currency={currency}>
    {moneyStr(amount, currency, formatString)}
  </span>
);

const moneyAccurate = (amount: number, currency: string): JSX.Element =>
  money(amount, currency, moneyAccurateFormat(currency));

export { moneyStr, currencySymbol, moneyAccurate };
export default money;
